import { SourceTypes } from '../../../../constants';
import { buildProxyURL, formatText, handleResponseError } from '../../utils';
import type { DataValue, PictureValueType, ProviderType } from '../types';
import type {
  VKontakteSourceValue,
  VkontakteResponseGroupType,
  VkontakteResponseProfileType,
  VkontakteResponseType
} from './types';
import { getVkontakteMedia } from './utils';
import {
  vkontaktePublicPageURLRegExp,
  vkontakteURLRegExp
} from './vkontakteURLPattern';

type VKDataValue = DataValue<string | PictureValueType[]>;

const PLATFORM = SourceTypes.VKONTAKTE;

const getUser = (
  fromId: number,
  info: Array<VkontakteResponseGroupType | VkontakteResponseProfileType>
) => {
  const user = info.find(({ id }) => id === Math.abs(fromId));

  if (!user) {
    return undefined;
  }

  const name =
    'name' in user ? user.name : `${user.first_name} ${user.last_name}`;

  return {
    name,
    image: user.photo_50,
    url: `https://vk.com/${user.screen_name}`
  };
};

const select = (response: VkontakteResponseType): VKDataValue[] => {
  handleResponseError(response, PLATFORM, 'response.items');

  const info = [...response.response.groups, ...response.response.profiles];

  return response.response.items.reduce<VKDataValue[]>(
    (
      acc,
      {
        id,
        date,
        attachments,
        likes,
        comments,
        text: rawText,
        from_id: fromId,
        copy_history: copyHistory
      }
    ) => {
      const text = formatText(rawText, PLATFORM);
      const user = getUser(fromId, info);

      const repostAttachments = copyHistory
        ?.flatMap(({ attachments }) => attachments)
        .filter(Boolean);
      const finalAttachments = repostAttachments?.length
        ? repostAttachments
        : attachments;
      const media = getVkontakteMedia(finalAttachments);

      if (text || media) {
        acc.push({
          text,
          user,
          media,
          id: `${id}`,
          timestamp: date * 1000,
          source: {
            type: PLATFORM,
            url: user ? `${user.url}?w=wall${fromId}_${id}` : ''
          },
          counters: {
            likes: likes?.count,
            comments: comments?.count
          }
        });
      }

      return acc;
    },
    []
  );
};

export function createVkontaktePageProvider(
  sourceValue: VKontakteSourceValue,
  globalKey: string[],
  filtersKey: string[]
): ProviderType<VkontakteResponseType> {
  const { source = '', connectionType, authentication: auth } = sourceValue;
  const { identity = '' } = auth ?? {};

  const key = [...globalKey, ...filtersKey, source, identity];
  let offset = 1;

  const page = (data: VkontakteResponseType | undefined) => {
    if (data?.response?.items?.length === 0) {
      return undefined;
    }

    const requestParams = new URLSearchParams({
      extended: '1',
      fields: 'addresses, screen_name, photo_50',
      v: '5.124'
    });

    if (connectionType === 'vkontaktePublic') {
      const [_all, ownerId] = source.match(vkontaktePublicPageURLRegExp) ?? [];

      requestParams.append('owner_id', `-${ownerId}`);
    }

    if (connectionType === 'vkontakteGroup') {
      const [_all, group] = source.match(vkontakteURLRegExp) ?? [];

      requestParams.append('domain', group);
    }

    if (connectionType === 'vkontakteProfile') {
      const [_all, profile] = source.match(vkontakteURLRegExp) ?? [];

      requestParams.append('domain', profile);
    }

    if (data) {
      offset += data.response.items.length;
      requestParams.append('offset', offset.toString());
    }

    const requestURL = `wall.get?${requestParams}`;

    return buildProxyURL({
      apiSource: PLATFORM,
      params: { q: requestURL, user_id: identity }
    });
  };

  return { key, page, select };
}
